import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Avatar,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Alert,
    Tooltip,
    Paper,
    Tabs,
    Tab,
    Fab,
    Drawer,
    Fade,
    Grid,
    AvatarGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    InputAdornment,
    useMediaQuery
} from '@mui/material';
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    People as PeopleIcon,
    Task as TaskIcon,
    Delete as DeleteIcon,
    Share as ShareIcon,
    Settings as SettingsIcon,
    ViewKanban as KanbanViewIcon,
    ViewList as ListViewIcon,
    CalendarMonth as CalendarViewIcon,
    ArrowBack as BackIcon,
    PersonAdd as InviteIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Assessment as MetricsIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Speed as SpeedIcon,
    AutoAwesome as AIStarsIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import TaskDetailPanel from '../components/TaskDetailPanel';
import { useCatAssistant } from '../contexts/CatAssistantContext';
import LynaAvatar from '../components/LynaAvatar';
import { useTheme } from '@mui/material';
import LynaInsightRenderer from '../components/LynaInsightRenderer';

interface Project {
    id: number;
    name: string;
    description: string;
    color: string;
    icon: string;
    owner: {
        id: number;
        name: string;
        email: string;
    };
    taskCounts: {
        total: number;
        todo: number;
        inProgress: number;
        done: number;
    };
    memberCount: number;
    lastActivity: string;
    role: 'owner' | 'admin' | 'collaborator' | 'viewer' | 'client';
}

interface CreateProjectData {
    name: string;
    description: string;
    color: string;
    icon: string;
    businessId?: number;
    isPublic: boolean;
}

interface Task {
    id: number;
    title: string;
    description: string;
    status: 'todo' | 'in_progress' | 'blocked' | 'done';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    position: number;
    assignedTo?: {
        id: number;
        name: string;
        email: string;
    };
    createdBy: {
        id: number;
        name: string;
        email: string;
    };
    startDate?: string;
    dueDate?: string;
    labels: string[];
    subtasks: any[];
    reservation?: {
        id: number;
        customerName: string;
        startDate: string;
        endDate: string;
    };
    client?: {
        id: number;
        name: string;
        email: string;
    };
    estimatedHours?: number;
    actualHours?: number;
    attachments?: any[];
    comments?: any[];
}

interface ProjectMember {
    id: number;
    role: string;
    user: {
        id: number;
        name: string;
        email: string;
    };
}

interface CreateTaskData {
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    assignedTo?: number;
    dueDate?: string;
    labels: string[];
}

interface InviteData {
    email: string;
    role: 'admin' | 'collaborator' | 'viewer' | 'client';
}

// --- Helper Components ---

const ProjectMembers: React.FC<{ projectMembers: ProjectMember[] }> = ({ projectMembers }) => (
    <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem' } }}>
        {projectMembers.map((member) => (
            <Tooltip key={member.id} title={`${member.user.name} (${member.role})`}>
                <Avatar sx={{ bgcolor: 'secondary.light' }}>
                    {member.user.name.charAt(0)}
                </Avatar>
            </Tooltip>
        ))}
    </AvatarGroup>
);

const TaskCard: React.FC<{ 
    task: Task, 
    provided: any, 
    snapshot: any, 
    onClick: () => void,
    priorityColor: string 
}> = ({ task, provided, snapshot, onClick, priorityColor }) => (
    <Paper
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        onClick={onClick}
        sx={{
            p: 2,
            borderRadius: '16px',
            cursor: 'pointer',
            borderLeft: `4px solid ${priorityColor}`,
            opacity: snapshot.isDragging ? 0.9 : 1,
            transform: snapshot.isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
            boxShadow: snapshot.isDragging ? '0 20px 40px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
                boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)'
            }
        }}
    >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'text.primary' }}>
            {task.title}
        </Typography>

        {task.description && (
            <Typography variant="body2" color="text.secondary" sx={{ 
                mb: 1.5, 
                display: '-webkit-box', 
                WebkitLineClamp: 2, 
                WebkitBoxOrient: 'vertical', 
                overflow: 'hidden',
                fontSize: '0.8rem'
            }}>
                {task.description}
            </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                    label={task.priority}
                    size="small"
                    sx={{
                        bgcolor: `${priorityColor}20`,
                        color: priorityColor,
                        fontWeight: 'bold',
                        height: 20,
                        fontSize: '0.65rem',
                        textTransform: 'uppercase'
                    }}
                />
                {(Array.isArray(task.labels) ? task.labels : []).slice(0, 2).map((label) => (
                    <Chip
                        key={label}
                        label={label}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', borderRadius: '4px' }}
                    />
                ))}
            </Box>

            {task.assignedTo?.name && (
                <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                    {task.assignedTo.name.charAt(0)}
                </Avatar>
            )}
        </Box>
        
        {task.subtasks && task.subtasks.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, height: 4, bgcolor: 'grey.100', borderRadius: 2, overflow: 'hidden' }}>
                    <Box 
                        sx={{ 
                            width: `${(task.subtasks.filter(s => s.completed).length / task.subtasks.length) * 100}%`,
                            height: '100%',
                            bgcolor: 'success.main'
                        }} 
                    />
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {task.subtasks.filter(s => s.completed).length}/{task.subtasks.length}
                </Typography>
            </Box>
        )}
    </Paper>
);

const TaskManager: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [tasks, setTasks] = useState<{
        todo: Task[];
        in_progress: Task[];
        blocked: Task[];
        done: Task[];
    }>({ todo: [], in_progress: [], blocked: [], done: [] });
    const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentView, setCurrentView] = useState<'board' | 'list' | 'calendar' | 'metrics'>('board');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPriority, setFilterPriority] = useState<string>('ALL');
    const [filterAssignee, setFilterAssignee] = useState<string>('ALL');
    const [metrics, setMetrics] = useState<any>(null);
    const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);

    // Dialogs and menus
    const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
    const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [projectMenuAnchor, setProjectMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedColumn, setSelectedColumn] = useState<string>('');
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
    const [taskDetailPanelOpen, setTaskDetailPanelOpen] = useState(false);
    const [lynaOpinion, setLynaOpinion] = useState<string | null>(null);
    const [isAnalyzingTasks, setIsAnalyzingTasks] = useState(false);

    // Loading states
    const [creatingProject, setCreatingProject] = useState(false);
    const [creatingTask, setCreatingTask] = useState(false);
    const [inviting, setInviting] = useState(false);

    // Form data
    const [newProject, setNewProject] = useState<CreateProjectData>({
        name: '',
        description: '',
        color: '#3B82F6',
        icon: '📁',
        isPublic: false
    });

    const [newTask, setNewTask] = useState<CreateTaskData>({
        title: '',
        description: '',
        priority: 'medium',
        labels: []
    });

    const [inviteData, setInviteData] = useState<InviteData>({
        email: '',
        role: 'collaborator'
    });

    const navigate = useNavigate();
    const { user } = useAuth();
    const { taskMoved, taskCompleted, taskCreated, motivate, celebrate, say } = useCatAssistant();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeMobileColumn, setActiveMobileColumn] = useState<string>('all');

    const priorityColors = {
        low: '#10B981',
        medium: '#3B82F6',
        high: '#F59E0B',
        urgent: '#EF4444'
    };

    const predefinedColors = [
        '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
        '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
        '#F97316', '#6366F1', '#14B8A6', '#F43F5E'
    ];

    const predefinedIcons = [
        '📁', '📋', '🎯', '💼', '🏠', '🚗',
        '💻', '📱', '🎨', '⚽', '🏨', '🍽️'
    ];

    const fetchLynaOpinion = async () => {
        if (!selectedProject) return;
        
        // Flatten all tasks except 'done'
        const pendingTasks = [...tasks.todo, ...tasks.in_progress, ...tasks.blocked];
        if (pendingTasks.length === 0) {
            say('¡Miau! No hay tareas pendientes para analizar. ¡Buen trabajo! 🐾✨', 'celebration');
            return;
        }

        try {
            setIsAnalyzingTasks(true);
            setLynaOpinion(null);
            const response = await axios.post('/api/ai/tasks/analyze', { tasks: pendingTasks });
            setLynaOpinion(response.data.analysis);
            say('¡He analizado tus tareas! Mira mi opinión. 🐈💡', 'tip');
        } catch (err) {
            console.error('Error fetching Lyna opinion:', err);
            say('Lo siento, mi cerebro gatuno se distrajo. 😿 Inténtalo de nuevo.', 'alert');
        } finally {
            setIsAnalyzingTasks(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        const handleTaskDataChanged = () => {
            fetchProjects();
            if (selectedProject?.id) {
                fetchProjectTasks(selectedProject.id);
            }
        };
        window.addEventListener('taskDataChanged', handleTaskDataChanged);
        return () => {
            window.removeEventListener('taskDataChanged', handleTaskDataChanged);
        };
    }, [selectedProject]);

    const fetchProjectTasks = async (projectId: number) => {
        try {
            const response = await axios.get(`/api/tasks/projects/${projectId}/tasks`);
            setTasks(response.data);
        } catch (err: any) {
            console.error('Error fetching tasks:', err);
            setError(err.response?.data?.error || 'Failed to load tasks');
        }
    };

    const fetchProjectMembers = async (projectId: number) => {
        try {
            const response = await axios.get(`/api/tasks/projects/${projectId}/members`);
            setProjectMembers(response.data);
        } catch (err: any) {
            console.error('Error fetching members:', err);
        }
    };

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/tasks/projects');
            setProjects(response.data);
            setError(null);

            // 🐱 Lyna motivates if there are many pending tasks!
            const totalPending = response.data.reduce((acc: number, p: Project) => 
                acc + (p.taskCounts.todo || 0) + (p.taskCounts.inProgress || 0), 0);
            if (totalPending > 0) {
                setTimeout(() => motivate(totalPending), 1500);
            }
        } catch (err: any) {
            console.error('Error fetching projects:', err);
            setError(err.response?.data?.error || 'Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProject = async () => {
        if (!newProject.name.trim()) {
            setError('Project name is required');
            return;
        }

        try {
            setCreatingProject(true);
            const response = await axios.post('/api/tasks/projects', newProject);
            setProjects([response.data, ...projects]);
            setCreateProjectDialogOpen(false);
            
            // 🐱 Lyna celebrates the new project!
            celebrate();
            say(`¡Miau! Un nuevo comienzo: "${response.data.name}" 📁✨`, 'celebration', 5000);

            setNewProject({
                name: '',
                description: '',
                color: '#3B82F6',
                icon: '📁',
                isPublic: false
            });
            setError(null);
        } catch (err: any) {
            console.error('Error creating project:', err);
            setError(err.response?.data?.error || 'Failed to create project');
        } finally {
            setCreatingProject(false);
        }
    };

    const handleOpenProjectMenu = (event: React.MouseEvent<HTMLElement>, project: Project) => {
        event.stopPropagation();
        setProjectMenuAnchor(event.currentTarget);
        setSelectedProject(project);
    };

    const handleCloseProjectMenu = () => {
        setProjectMenuAnchor(null);
        setSelectedProject(null);
    };

    const handleOpenProject = (project: Project) => {
        navigate(`/tasks/project/${project.id}`);
    };

    const handleDeleteProject = async () => {
        if (!selectedProject) return;

        try {
            await axios.delete(`/api/tasks/projects/${selectedProject.id}`);
            setProjects(projects.filter(p => p.id !== selectedProject.id));
            setSelectedProject(null);
            handleCloseProjectMenu();
        } catch (err: any) {
            console.error('Error deleting project:', err);
            setError(err.response?.data?.error || 'Failed to delete project');
        }
    };

    const handleCreateTask = async () => {
        if (!newTask.title.trim() || !selectedColumn || !selectedProject) {
            setError('Task title, column, and project are required');
            return;
        }

        try {
            setCreatingTask(true);
            const response = await axios.post(`/api/tasks/projects/${selectedProject.id}/tasks`, {
                ...newTask,
                status: selectedColumn
            });

            // Add task to the appropriate column
            setTasks(prev => ({
                ...prev,
                [selectedColumn]: [...prev[selectedColumn as keyof typeof prev], response.data]
            }));

            setCreateTaskDialogOpen(false);
            setNewTask({
                title: '',
                description: '',
                priority: 'medium',
                labels: []
            });
            setSelectedColumn('');
            setError(null);

            // 🐱 Cat celebration for new task!
            taskCreated(response.data.title);
        } catch (err: any) {
            console.error('Error creating task:', err);
            setError(err.response?.data?.error || 'Failed to create task');
        } finally {
            setCreatingTask(false);
        }
    };

    const handleApplyAIAction = async (type: string, data: any) => {
        if (!selectedProject) return;

        if (type === 'CREATE_TASK') {
            try {
                setIsAnalyzingTasks(true); // Reuse loading state for the action
                const response = await axios.post(`/api/tasks/projects/${selectedProject.id}/tasks`, {
                    title: data.title,
                    description: data.description || 'Tarea sugerida por Lyna 🐈✨',
                    priority: data.priority || 'medium',
                    status: 'todo'
                });

                // Add to local state
                setTasks(prev => ({
                    ...prev,
                    todo: [...prev.todo, response.data]
                }));

                // 🐱 Cat reaction
                taskCreated(response.data.title);
                say(`¡Listo! He creado la tarea "${response.data.title}" por ti. 🐾`, 'success');
                
                // Optional: clear the opinion or just scroll to mark it as done?
                // For now, let's just keep it.
            } catch (err) {
                console.error('Error creating task from AI:', err);
                say('Miau... hubo un error al crear la tarea. 😿', 'alert');
            } finally {
                setIsAnalyzingTasks(false);
            }
        }
    };

    const handleInvite = async () => {
        if (!inviteData.email.trim() || !selectedProject) {
            setError('Email and project are required');
            return;
        }

        try {
            setInviting(true);
            await axios.post(`/api/tasks/projects/${selectedProject.id}/invite`, inviteData);

            setInviteDialogOpen(false);
            setInviteData({ email: '', role: 'collaborator' });
            setError(null);
            // Refresh members
            fetchProjectMembers(selectedProject.id);
        } catch (err: any) {
            console.error('Error inviting user:', err);
            setError(err.response?.data?.error || 'Failed to invite user');
        } finally {
            setInviting(false);
        }
    };

    const handleDragEnd = async (result: any) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const sourceColumn = source.droppableId;
        const destColumn = destination.droppableId;
        const taskId = parseInt(draggableId);

        // Get task title for cat message
        const movedTaskTitle = tasks[sourceColumn as keyof typeof tasks]?.[source.index]?.title || 'Tarea';

        // Update UI immediately (Optimistic Update)
        setTasks(prev => {
            const newTasks = { ...prev };
            
            if (sourceColumn === destColumn) {
                // Same column reordering
                const columnTasks = Array.from(newTasks[sourceColumn as keyof typeof newTasks]);
                const [movedTask] = columnTasks.splice(source.index, 1);
                columnTasks.splice(destination.index, 0, movedTask);
                
                return {
                    ...newTasks,
                    [sourceColumn]: columnTasks
                };
            } else {
                // Moving between columns
                const sourceTasks = Array.from(newTasks[sourceColumn as keyof typeof newTasks]);
                const [movedTask] = sourceTasks.splice(source.index, 1);

                const destTasks = Array.from(newTasks[destColumn as keyof typeof newTasks]);
                destTasks.splice(destination.index, 0, { ...movedTask, status: destColumn as any });

                return {
                    ...newTasks,
                    [sourceColumn]: sourceTasks,
                    [destColumn]: destTasks
                };
            }
        });

        try {
            await axios.put(`/api/tasks/${taskId}/move`, {
                status: destColumn,
                position: destination.index
            });

            // 🐱 Cat reactions!
            if (destColumn === 'done') {
                // Epic celebration for completed task!
                taskCompleted(movedTaskTitle);
            } else if (destColumn === 'blocked') {
                // Alert for blocked tasks :(
                say(`¡Miau! "${movedTaskTitle}" se ha bloqueado. ¿Necesitas ayuda? 🚨`, 'alert', 5000);
            } else {
                // Regular move notification
                taskMoved(movedTaskTitle, destColumn);
            }
        } catch (err: any) {
            console.error('Error moving task:', err);
            // Revert UI on error
            if (selectedProject) {
                fetchProjectTasks(selectedProject.id);
            }
        }
    };

    const openCreateTaskDialog = (columnId: string) => {
        setSelectedColumn(columnId);
        setCreateTaskDialogOpen(true);
    };

    const handleTaskClick = (taskId: number) => {
        setSelectedTaskId(taskId);
        setTaskDetailPanelOpen(true);
    };

    const handleTaskUpdate = (updatedTask: Task) => {
        // Update the task in the local state
        setTasks(prev => ({
            ...prev,
            [updatedTask.status]: prev[updatedTask.status as keyof typeof prev].map(task =>
                task.id === updatedTask.id ? updatedTask : task
            )
        }));
    };

    const handleTaskDetailPanelClose = () => {
        setTaskDetailPanelOpen(false);
        setSelectedTaskId(null);
    };

    const getProjectProgress = (project: Project) => {
        const { todo, inProgress, done } = project.taskCounts;
        const total = todo + inProgress + done;
        return total > 0 ? Math.round((done / total) * 100) : 0;
    };

    const getStatusColor = (progress: number) => {
        if (progress >= 80) return 'success';
        if (progress >= 50) return 'warning';
        return 'error';
    };

    const getPriorityColor = (priority: string) => {
        return priorityColors[priority as keyof typeof priorityColors] || '#6B7280';
    };

    const formatLastActivity = (dateString: string) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

            if (diffInHours < 1) return 'Hace menos de 1 hora';
            if (diffInHours < 24) return `Hace ${diffInHours} horas`;
            if (diffInHours < 48) return 'Ayer';
            return format(date, 'dd MMM yyyy', { locale: es });
        } catch {
            return 'Fecha desconocida';
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, width: { xs: '100%', md: 'auto' } }}>
                    <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                        <LynaAvatar size={36} /> Purrr Hacer
                    </Typography>
                    {selectedProject && (
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                            <InputLabel>Proyecto</InputLabel>
                            <Select
                                value={selectedProject.id}
                                onChange={(e) => {
                                    const projectId = parseInt(e.target.value as unknown as string);
                                    const project = projects.find(p => p.id === projectId);
                                    if (project) {
                                        setSelectedProject(project);
                                        fetchProjectTasks(project.id);
                                    }
                                }}
                                label="Proyecto"
                            >
                                {projects.map((project) => (
                                    <MenuItem key={project.id} value={project.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ fontSize: '1rem' }}>{project.icon}</Box>
                                            {project.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
                    {selectedProject && (
                        <>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={isAnalyzingTasks ? <CircularProgress size={18} color="inherit" /> : <Box sx={{ fontSize: '1.1rem' }}>💡</Box>}
                                onClick={fetchLynaOpinion}
                                disabled={isAnalyzingTasks}
                                sx={{ 
                                    flex: { xs: '1 1 calc(50% - 4px)', sm: 'initial' },
                                    borderRadius: 3, 
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    textTransform: 'none',
                                    background: 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
                                    boxShadow: '0 4px 15px rgba(255, 107, 157, 0.3)',
                                    '&:hover': { transform: 'scale(1.03)' },
                                    transition: 'all 0.2s'
                                }}
                            >
                                {isAnalyzingTasks ? 'Analizando...' : 'Consultar a Lyna'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<InviteIcon fontSize="small" />}
                                onClick={() => setInviteDialogOpen(true)}
                                sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: 'initial' }, borderRadius: 3, textTransform: 'none', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                                Invitar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon fontSize="small" />}
                                onClick={() => openCreateTaskDialog('todo')}
                                sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: 'initial' }, borderRadius: 3, textTransform: 'none', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                                Nueva Tarea
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => setCreateProjectDialogOpen(true)}
                        sx={{ flex: { xs: '1 1 100%', sm: 'initial' }, borderRadius: 3, textTransform: 'none', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                        Nuevo Proyecto
                    </Button>
                </Box>
            </Box>

            {lynaOpinion && selectedProject && (
                <Fade in timeout={1000}>
                    <Paper 
                        sx={{ 
                            p: 3, 
                            mb: 4, 
                            borderRadius: '24px', 
                            bgcolor: isDark ? 'rgba(155, 89, 182, 0.1)' : '#FFFFFF',
                            border: '1px solid rgba(155, 89, 182, 0.3)',
                            boxShadow: '0 15px 40px rgba(155, 89, 182, 0.2)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '4px',
                                height: '100%',
                                bgcolor: 'secondary.main',
                            }
                        }}
                    >
                        <IconButton 
                            size="small" 
                            onClick={() => setLynaOpinion(null)} 
                            sx={{ position: 'absolute', top: 12, right: 12, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Stack direction="row" spacing={3} alignItems="flex-start">
                            <Box sx={{ 
                                p: 0.5, 
                                bgcolor: 'white', 
                                borderRadius: '50%', 
                                boxShadow: '0 4px 15px rgba(155, 89, 182, 0.4)',
                                border: '3px solid',
                                borderColor: 'secondary.main'
                            }}>
                                <LynaAvatar size={60} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    La Inteligencia de Lyna 🐈✨
                                    <Chip label="Análisis en vivo" size="small" variant="outlined" sx={{ borderColor: 'secondary.main', color: 'secondary.main', fontWeight: 'bold', height: 20, fontSize: '0.65rem' }} />
                                </Typography>
                                <LynaInsightRenderer 
                                    content={lynaOpinion} 
                                    onApplyAction={handleApplyAIAction}
                                />
                            </Box>
                        </Stack>
                    </Paper>
                </Fade>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {projects.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <TaskIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                        ¡Tu tablero está de miau! 🐾
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                        Parece que aún no tienes proyectos. ¡Crea uno para empezar a organizar tus tareas hoy mismo!
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateProjectDialogOpen(true)}
                        sx={{ 
                            borderRadius: '16px', 
                            px: 4, 
                            py: 1.5,
                            boxShadow: '0 8px 32px rgba(155, 89, 182, 0.2)',
                            background: 'linear-gradient(135deg, #A78BFA 0%, #F472B6 100%)'
                        }}
                    >
                        Crear Mi Primer Proyecto
                    </Button>
                </Box>
            ) : !selectedProject ? (
                <Fade in timeout={800}>
                    <Box sx={{ py: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
                            🚀 ¿En qué proyecto quieres trabajar hoy?
                        </Typography>
                        <Grid container spacing={3} justifyContent="center">
                            {projects.map((project) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={project.id}>
                                    <Card 
                                        sx={{ 
                                            cursor: 'pointer', 
                                            height: '100%',
                                            borderRadius: '24px',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            border: '2px solid transparent',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                borderColor: 'primary.main',
                                                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                                            }
                                        }}
                                        onClick={() => {
                                            setSelectedProject(project);
                                            fetchProjectTasks(project.id);
                                            say(`¡Excelente elección! Trabajemos en "${project.name}" 😸✨`, 'greeting', 5000);
                                        }}
                                    >
                                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                            <Box 
                                                sx={{ 
                                                    fontSize: '3rem', 
                                                    mb: 2, 
                                                    display: 'flex', 
                                                    justifyContent: 'center',
                                                    p: 2,
                                                    bgcolor: `${project.color}15`,
                                                    borderRadius: '50%',
                                                    width: 80,
                                                    height: 80,
                                                    mx: 'auto'
                                                }}
                                            >
                                                {project.icon}
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                {project.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 40 }}>
                                                {project.description || 'Sin descripción'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                <Chip 
                                                    label={`${project.taskCounts.total} tareas`} 
                                                    size="small" 
                                                    sx={{ borderRadius: '8px' }}
                                                />
                                                <Chip 
                                                    label={`${project.memberCount} miembros`} 
                                                    size="small" 
                                                    variant="outlined"
                                                    sx={{ borderRadius: '8px' }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Fade>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 200px)', gap: 3 }}>
                    {/* Toolbar & View Options */}
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: 2, 
                            borderRadius: '20px', 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            gap: 2,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                            background: isDark ? 'rgba(30,30,30,0.8)' : 'rgba(255,255,255,0.8)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        <Tabs 
                            value={currentView} 
                            onChange={(e, newValue) => {
                                setCurrentView(newValue);
                                if (newValue === 'metrics' && selectedProject) {
                                    axios.get(`/api/tasks/projects/${selectedProject.id}/metrics`)
                                        .then(res => setMetrics(res.data))
                                        .catch(err => console.error(err));
                                }
                            }}
                            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, borderRadius: '12px', minWidth: 100, fontWeight: 700 } }}
                        >
                            <Tab icon={<KanbanViewIcon sx={{ fontSize: 18 }} />} label="Tablero" value="board" iconPosition="start" />
                            <Tab icon={<ListViewIcon sx={{ fontSize: 18 }} />} label="Lista" value="list" iconPosition="start" />
                            <Tab icon={<CalendarViewIcon sx={{ fontSize: 18 }} />} label="Calendario" value="calendar" iconPosition="start" />
                            <Tab icon={<MetricsIcon sx={{ fontSize: 18 }} />} label="Métricas" value="metrics" iconPosition="start" />
                        </Tabs>

                        {/* Search and Filters Bar */}
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Buscar tareas..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />

                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={filterPriority}
                                    onChange={(e) => setFilterPriority(e.target.value)}
                                    sx={{ borderRadius: '12px', fontSize: '0.82rem' }}
                                >
                                    <MenuItem value="ALL">Todas prioridades</MenuItem>
                                    <MenuItem value="urgent">🔴 Urgente</MenuItem>
                                    <MenuItem value="high">🟠 Alta</MenuItem>
                                    <MenuItem value="medium">🟡 Media</MenuItem>
                                    <MenuItem value="low">🟢 Baja</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={filterAssignee}
                                    onChange={(e) => setFilterAssignee(e.target.value)}
                                    sx={{ borderRadius: '12px', fontSize: '0.82rem' }}
                                >
                                    <MenuItem value="ALL">Todos asignados</MenuItem>
                                    <MenuItem value="MINE">👤 Mis tareas</MenuItem>
                                    {projectMembers.map(m => (
                                        <MenuItem key={m.user.id} value={m.user.id}>
                                            {m.user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <ProjectMembers projectMembers={projectMembers} />
                        </Box>
                    </Paper>

                    {/* VISTA 1: TABLERO (KANBAN) */}
                    {currentView === 'board' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Mobile Column Quick Selector */}
                            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, overflowX: 'auto', pb: 1 }}>
                                {[
                                    { id: 'all', name: 'Todas', icon: '📋' },
                                    { id: 'todo', name: 'Por Hacer', icon: '📝' },
                                    { id: 'in_progress', name: 'En Progreso', icon: '⚡' },
                                    { id: 'blocked', name: 'Bloqueado', icon: '⚠️' },
                                    { id: 'done', name: 'Completado', icon: '✅' }
                                ].map(c => (
                                    <Chip
                                        key={c.id}
                                        label={`${c.icon} ${c.name}`}
                                        clickable
                                        color={activeMobileColumn === c.id ? 'primary' : 'default'}
                                        variant={activeMobileColumn === c.id ? 'filled' : 'outlined'}
                                        onClick={() => setActiveMobileColumn(c.id)}
                                        sx={{ fontWeight: 700, borderRadius: '12px' }}
                                    />
                                ))}
                            </Box>

                            <DragDropContext key={selectedProject.id} onDragEnd={handleDragEnd}>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        gap: 2, 
                                        overflowX: 'auto', 
                                        pb: 2, 
                                        flex: 1,
                                        scrollSnapType: { xs: 'x mandatory', sm: 'none' },
                                        '&::-webkit-scrollbar': { height: 6 },
                                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 4 }
                                    }}
                                >
                                    {[
                                        { id: 'todo', name: 'Por Hacer', color: '#6B7280', icon: '📝' },
                                        { id: 'in_progress', name: 'En Progreso', color: '#3B82F6', icon: '⚡' },
                                        { id: 'blocked', name: 'Bloqueado', color: '#EF4444', icon: '⚠️' },
                                        { id: 'done', name: 'Completado', color: '#10B981', icon: '✅' }
                                    ]
                                    .filter(col => activeMobileColumn === 'all' || activeMobileColumn === col.id)
                                    .map((column) => {
                                        const colTasks = (tasks[column.id as keyof typeof tasks] || []).filter(t => {
                                            if (searchQuery.trim()) {
                                                const q = searchQuery.toLowerCase();
                                                if (!t.title.toLowerCase().includes(q) && (!t.description || !t.description.toLowerCase().includes(q))) return false;
                                            }
                                            if (filterPriority !== 'ALL' && t.priority !== filterPriority) return false;
                                            if (filterAssignee === 'MINE' && t.assignedTo?.id !== user?.id) return false;
                                            if (filterAssignee !== 'ALL' && filterAssignee !== 'MINE' && t.assignedTo?.id !== Number(filterAssignee)) return false;
                                            return true;
                                        });

                                        return (
                                            <Box
                                                key={column.id}
                                                sx={{
                                                    minWidth: { xs: activeMobileColumn === 'all' ? '85vw' : '100%', sm: 320 },
                                                    maxWidth: { xs: activeMobileColumn === 'all' ? '85vw' : '100%', sm: 320 },
                                                    scrollSnapAlign: { xs: 'center', sm: 'none' },
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    height: '100%',
                                                    borderRadius: '24px',
                                                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                                    border: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.04)',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                            <Box 
                                                sx={{ 
                                                    p: 2, 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    borderBottom: `2px solid ${column.color}40`,
                                                    bgcolor: `${column.color}08`
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <span>{column.icon}</span> {column.name}
                                                    </Typography>
                                                    <Chip
                                                        label={colTasks.length}
                                                        size="small"
                                                        sx={{ 
                                                            bgcolor: column.color, 
                                                            color: 'white', 
                                                            fontWeight: 'bold',
                                                            height: 20,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openCreateTaskDialog(column.id)}
                                                    sx={{ '&:hover': { bgcolor: `${column.color}20` } }}
                                                >
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </Box>

                                            <Droppable droppableId={column.id} isDropDisabled={false}>
                                                {(provided, snapshot) => (
                                                    <Box
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                        sx={{
                                                            p: 1.5,
                                                            flex: 1,
                                                            overflowY: 'auto',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 1.5,
                                                            bgcolor: snapshot.isDraggingOver ? `${column.color}10` : 'transparent',
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                        }}
                                                    >
                                                        {colTasks.length === 0 ? (
                                                            <Box sx={{ py: 6, textAlign: 'center', opacity: 0.5, border: '2px dashed rgba(0,0,0,0.1)', borderRadius: '16px' }}>
                                                                <Typography variant="body2">Nada por aquí aún 🐾</Typography>
                                                            </Box>
                                                        ) : (
                                                            colTasks.map((task, index) => (
                                                                <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
                                                                    {(provided, snapshot) => (
                                                                        <TaskCard 
                                                                            task={task} 
                                                                            provided={provided} 
                                                                            snapshot={snapshot}
                                                                            onClick={() => handleTaskClick(task.id)}
                                                                            priorityColor={getPriorityColor(task.priority)}
                                                                        />
                                                                    )}
                                                                </Draggable>
                                                            ))
                                                        )}
                                                        {provided.placeholder}
                                                    </Box>
                                                )}
                                            </Droppable>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </DragDropContext>
                    </Box>
                )}

                    {/* VISTA 2: LISTA DE TAREAS */}
                    {currentView === 'list' && (
                        <TableContainer component={Paper} sx={{ borderRadius: '24px', boxShadow: 'none', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Tarea</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Prioridad</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Vencimiento</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Asignado</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...tasks.todo, ...tasks.in_progress, ...tasks.blocked, ...tasks.done]
                                        .filter(t => {
                                            if (searchQuery.trim()) {
                                                const q = searchQuery.toLowerCase();
                                                if (!t.title.toLowerCase().includes(q) && (!t.description || !t.description.toLowerCase().includes(q))) return false;
                                            }
                                            if (filterPriority !== 'ALL' && t.priority !== filterPriority) return false;
                                            if (filterAssignee === 'MINE' && t.assignedTo?.id !== user?.id) return false;
                                            if (filterAssignee !== 'ALL' && filterAssignee !== 'MINE' && t.assignedTo?.id !== Number(filterAssignee)) return false;
                                            return true;
                                        })
                                        .map((task) => (
                                            <TableRow key={task.id} hover onClick={() => handleTaskClick(task.id)} sx={{ cursor: 'pointer' }}>
                                                <TableCell>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textDecoration: task.status === 'done' ? 'line-through' : 'none', color: task.status === 'done' ? 'text.secondary' : 'text.primary' }}>
                                                        {task.title}
                                                    </Typography>
                                                    {task.description && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                            {task.description}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={task.status === 'todo' ? 'Por Hacer' : task.status === 'in_progress' ? 'En Progreso' : task.status === 'blocked' ? 'Bloqueada' : 'Completada'}
                                                        color={task.status === 'done' ? 'success' : task.status === 'in_progress' ? 'primary' : task.status === 'blocked' ? 'error' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={task.priority.toUpperCase()}
                                                        sx={{ bgcolor: `${getPriorityColor(task.priority)}22`, color: getPriorityColor(task.priority), fontWeight: 700, height: 20, fontSize: '0.65rem' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {task.dueDate ? (
                                                        <Typography variant="caption" color={new Date(task.dueDate) < new Date() && task.status !== 'done' ? 'error.main' : 'text.secondary'}>
                                                            📅 {new Date(task.dueDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {task.assignedTo ? (
                                                        <Chip avatar={<Avatar>{task.assignedTo.name.charAt(0)}</Avatar>} label={task.assignedTo.name} size="small" variant="outlined" />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">Sin asignar</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button size="small" onClick={(e) => { e.stopPropagation(); handleTaskClick(task.id); }}>
                                                        Ver Detalle
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* VISTA 3: CALENDARIO */}
                    {currentView === 'calendar' && (
                        <Paper sx={{ p: 3, borderRadius: '24px', bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'white', border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                📅 Calendario de Entregables
                            </Typography>
                            <Grid container spacing={2}>
                                {[...tasks.todo, ...tasks.in_progress, ...tasks.blocked]
                                    .filter(t => t.dueDate)
                                    .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
                                    .map(t => (
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
                                            <Card sx={{ p: 2, borderRadius: 3, borderLeft: `4px solid ${getPriorityColor(t.priority)}`, cursor: 'pointer' }} onClick={() => handleTaskClick(t.id)}>
                                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>
                                                    📅 {new Date(t.dueDate!).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' })}
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5 }}>{t.title}</Typography>
                                                <Chip size="small" label={t.priority} sx={{ mt: 1, height: 18, fontSize: '0.65rem', bgcolor: `${getPriorityColor(t.priority)}22`, color: getPriorityColor(t.priority) }} />
                                            </Card>
                                        </Grid>
                                    ))}
                            </Grid>
                        </Paper>
                    )}

                    {/* VISTA 4: MÉTRICAS Y DASHBOARD */}
                    {currentView === 'metrics' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Paper sx={{ p: 2.5, borderRadius: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                                        <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>Avance General</Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 900, my: 1 }}>{metrics?.progressPercent ?? getProjectProgress(selectedProject)}%</Typography>
                                        <LinearProgress variant="determinate" value={metrics?.progressPercent ?? getProjectProgress(selectedProject)} sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Paper sx={{ p: 2.5, borderRadius: 4, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Tareas Totales</Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 900, my: 1 }}>{metrics?.total ?? selectedProject.taskCounts.total}</Typography>
                                        <Typography variant="caption" color="text.secondary">En este proyecto</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Paper sx={{ p: 2.5, borderRadius: 4, textAlign: 'center' }}>
                                        <Typography variant="caption" color="success.main" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Completadas</Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 900, my: 1, color: 'success.main' }}>{metrics?.done ?? selectedProject.taskCounts.done}</Typography>
                                        <Typography variant="caption" color="text.secondary">¡Gran trabajo!</Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Paper sx={{ p: 2.5, borderRadius: 4, textAlign: 'center' }}>
                                        <Typography variant="caption" color="error.main" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Vencidas</Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 900, my: 1, color: 'error.main' }}>{metrics?.overdue ?? 0}</Typography>
                                        <Typography variant="caption" color="text.secondary">Requieren atención</Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Priority distribution breakdown */}
                            <Paper sx={{ p: 3, borderRadius: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Distribución por Prioridad</Typography>
                                <Grid container spacing={2}>
                                    {Object.entries(priorityColors).map(([pKey, pColor]) => {
                                        const count = metrics?.priorityBreakdown?.[pKey] || [...tasks.todo, ...tasks.in_progress, ...tasks.blocked, ...tasks.done].filter(t => t.priority === pKey).length;
                                        const totalT = metrics?.total || 1;
                                        const pct = Math.round((count / (totalT || 1)) * 100);
                                        return (
                                            <Grid size={{ xs: 12, sm: 6 }} key={pKey}>
                                                <Box sx={{ mb: 1 }}>
                                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: pColor, textTransform: 'capitalize' }}>● {pKey}</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{count} tareas ({pct}%)</Typography>
                                                    </Stack>
                                                    <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: `${pColor}22`, '& .MuiLinearProgress-bar': { bgcolor: pColor } }} />
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}

            {/* Create Project Dialog */}
            <Dialog open={createProjectDialogOpen} onClose={() => setCreateProjectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Crear Nuevo Proyecto</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Nombre del Proyecto"
                            value={newProject.name}
                            onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Descripción"
                            multiline
                            rows={3}
                            value={newProject.description}
                            onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                            margin="normal"
                        />
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Icono</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {predefinedIcons.map(icon => (
                                    <IconButton
                                        key={icon}
                                        onClick={() => setNewProject({ ...newProject, icon })}
                                        sx={{ 
                                            border: newProject.icon === icon ? '2px solid' : 'none',
                                            borderColor: 'primary.main'
                                        }}
                                    >
                                        {icon}
                                    </IconButton>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Color</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {predefinedColors.map(color => (
                                    <Box
                                        key={color}
                                        onClick={() => setNewProject({ ...newProject, color })}
                                        sx={{ 
                                            width: 32, 
                                            height: 32, 
                                            bgcolor: color, 
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            border: newProject.color === color ? '2px solid white' : 'none',
                                            boxShadow: newProject.color === color ? 2 : 0
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateProjectDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button onClick={handleCreateProject} variant="contained" disabled={creatingProject}>
                        {creatingProject ? <CircularProgress size={20} /> : 'Crear Proyecto'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Task Dialog */}
            <Dialog open={createTaskDialogOpen} onClose={() => setCreateTaskDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nueva Tarea en {selectedColumn.replace('_', ' ')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Título de la tarea"
                            value={newTask.title}
                            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Descripción"
                            multiline
                            rows={3}
                            value={newTask.description}
                            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                            margin="normal"
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Prioridad</InputLabel>
                            <Select
                                value={newTask.priority}
                                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                                label="Prioridad"
                            >
                                <MenuItem value="low">Baja</MenuItem>
                                <MenuItem value="medium">Media</MenuItem>
                                <MenuItem value="high">Alta</MenuItem>
                                <MenuItem value="urgent">Urgente</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Fecha de vencimiento"
                            type="date"
                            value={newTask.dueDate}
                            onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateTaskDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button onClick={handleCreateTask} variant="contained" disabled={creatingTask}>
                        {creatingTask ? <CircularProgress size={20} /> : 'Crear Tarea'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Invite User Dialog */}
            <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Invitar Usuario al Proyecto</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Email del usuario"
                            type="email"
                            value={inviteData.email}
                            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                            margin="normal"
                            required
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Rol</InputLabel>
                            <Select
                                value={inviteData.role}
                                onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as any })}
                                label="Rol"
                            >
                                <MenuItem value="collaborator">Colaborador</MenuItem>
                                <MenuItem value="viewer">Solo lectura</MenuItem>
                                <MenuItem value="admin">Administrador</MenuItem>
                                <MenuItem value="client">Cliente</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInviteDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleInvite}
                        variant="contained"
                        disabled={inviting || !inviteData.email.trim()}
                    >
                        {inviting ? <CircularProgress size={20} /> : 'Enviar Invitación'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Task Detail Panel */}
            <TaskDetailPanel
                taskId={selectedTaskId}
                open={taskDetailPanelOpen}
                onClose={handleTaskDetailPanelClose}
                onUpdate={handleTaskUpdate}
            />
        </Container>
    );
};

export default TaskManager;